import { useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import {
  FaBus,
  FaCalendarAlt,
  FaHotel,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaMountain,
  FaRoute,
  FaSatellite,
  FaShoppingBag,
  FaStar,
  FaUtensils,
} from "react-icons/fa";
import NavBar from "./nav";
import Footer from "./footer";
import fallbackImages from "../assets/images";
import { placesToVisit } from "./placeData";
import { journeyItems } from "./journeyData";
import {
  fetchAccommodations,
  fetchAttractionsites,
  fetchBuses,
  fetchEvents,
  fetchMalls,
  fetchResturants,
} from "../app/dbSlice";
import { getDistanceFromKimberley, getItemCoordinates, getOpenStatus } from "../utils/smartFilters";
import "leaflet/dist/leaflet.css";
import "../css/mapView.css";

const kimberleyCenter = { lat: -28.7383, lng: 24.7639 };

const mapTypes = {
  street: {
    label: "Street",
    icon: FaLayerGroup,
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  satellite: {
    label: "Satellite",
    icon: FaSatellite,
    url:
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
  },
  terrain: {
    label: "Terrain",
    icon: FaMountain,
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
  },
};

const categoryIcons = {
  Accommodation: FaHotel,
  Attractions: FaMapMarkerAlt,
  Buses: FaBus,
  Events: FaCalendarAlt,
  Journey: FaRoute,
  Places: FaMapMarkerAlt,
  Restaurants: FaUtensils,
  "Shopping Malls": FaShoppingBag,
};

const getFallbackImages = (itemName) => {
  if (!itemName) return [];

  const matchKey = Object.keys(fallbackImages).find(
    (key) =>
      key.toLowerCase().includes(itemName.toLowerCase()) ||
      itemName.toLowerCase().includes(key.toLowerCase())
  );

  return matchKey ? fallbackImages[matchKey] : [];
};

const getDetailPath = (category) => {
  if (category === "Accommodation") return "/description";
  if (category === "Events") return "/eventDescription";
  if (category === "Restaurants") return "/resturantDescription";
  if (category === "Shopping Malls") return "/mallDescription";
  if (category === "Attractions") return "/attractionDescription";
  if (category === "Buses") return "/busDescription";
  return "";
};

const createPinIcon = (Icon, isActive) =>
  L.divIcon({
    html: renderToStaticMarkup(
      <span className={`leaflet-pin-inner${isActive ? " active" : ""}`}>
        <Icon />
      </span>
    ),
    className: "leaflet-custom-pin",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -20],
  });

const FlyToPlace = ({ place }) => {
  const map = useMap();

  useEffect(() => {
    if (place?.coordinates) {
      map.flyTo([place.coordinates.lat, place.coordinates.lng], Math.max(map.getZoom(), 13), {
        duration: 0.6,
      });
    }
  }, [place, map]);

  return null;
};

const normalizeCollectionItem = (item, category) => {
  const gallery = getFallbackImages(item.name);

  return {
    ...item,
    title: item.name || item.title || "Untitled",
    category,
    image: gallery[0] || item.image || "",
    gallery,
    detailPath: getDetailPath(category),
    detailState: {
      title: item.name || item.title || "Untitled",
      description: item.description || "",
      image: gallery[0] || item.image || "",
      gallery,
      rating: Number(item.rating) || 0,
      location: item.location || "",
      category: item.category || category,
      price: item.pricePerNight || item.price || 0,
      pricePerNight: item.pricePerNight || item.price || 0,
      phone: item.phone || "",
      email: item.email || "",
      mapLink: item.mapLink,
      ticketLink: item.ticketLink,
      amenities: Array.isArray(item.amenities) ? item.amenities : [],
      lineUp: Array.isArray(item.lineUp) ? item.lineUp : [],
      shops: Array.isArray(item.shops) ? item.shops : [],
      routes: Array.isArray(item.routes) ? item.routes : [],
      schedule: item.schedule || [],
      operator: item.operator || "",
      openingHours: item.openingHours || "",
      hours: item.hours || "",
    },
  };
};

const MapView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [mapType, setMapType] = useState("street");

  const {
    accommodations = [],
    events = [],
    restaurants = [],
    attractionsites = [],
    malls = [],
    buses = [],
  } = useSelector((state) => state.db);

  useEffect(() => {
    dispatch(fetchAccommodations());
    dispatch(fetchEvents());
    dispatch(fetchResturants());
    dispatch(fetchAttractionsites());
    dispatch(fetchMalls());
    dispatch(fetchBuses());
  }, [dispatch]);

  const mapItems = useMemo(() => {
    const collectionItems = [
      ...accommodations.map((item) => normalizeCollectionItem(item, "Accommodation")),
      ...events.map((item) => normalizeCollectionItem(item, "Events")),
      ...restaurants.map((item) => normalizeCollectionItem(item, "Restaurants")),
      ...attractionsites.map((item) => normalizeCollectionItem(item, "Attractions")),
      ...malls.map((item) => normalizeCollectionItem(item, "Shopping Malls")),
      ...buses.map((item) => normalizeCollectionItem(item, "Buses")),
    ];

    const placeItems = placesToVisit.map((place) => ({
      ...place,
      title: place.name,
      category: "Places",
      detailPath: `/places/${place.slug}`,
      detailState: { place },
    }));

    const journeyMapItems = journeyItems.map((item) => ({
      ...item,
      title: item.name,
      category: "Journey",
      detailPath: `/journey/${item.slug}`,
      detailState: { item },
    }));

    return [...collectionItems, ...placeItems, ...journeyMapItems].map((item) => {
      const coordinates = getItemCoordinates(item);
      return {
        ...item,
        coordinates,
        distanceKm: getDistanceFromKimberley(item),
        openStatus: getOpenStatus(item),
      };
    });
  }, [accommodations, attractionsites, buses, events, malls, restaurants]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(mapItems.map((item) => item.category))).sort()],
    [mapItems]
  );

  const filteredItems = useMemo(
    () =>
      activeCategory === "All"
        ? mapItems
        : mapItems.filter((item) => item.category === activeCategory),
    [activeCategory, mapItems]
  );

  const placedItems = filteredItems.filter((item) => item.coordinates);
  const unplacedItems = filteredItems.filter((item) => !item.coordinates);
  const activePlace = selectedPlace || placedItems[0] || filteredItems[0];

  const openDetails = (item) => {
    if (!item?.detailPath) return;
    navigate(item.detailPath, { state: item.detailState || {} });
  };

  return (
    <div className="map-view-page">
      <NavBar />

      <section className="hero-section map-view-hero">
        <div className="hero-content">
          <span className="hero-eyebrow">Kimberley Map</span>
          <h1 className="hero-title">Explore The City By Location</h1>
          <p className="hero-subtitle">
            See stays, food, attractions, events, and transport options together
            so visitors can plan by area, distance, and convenience.
          </p>
        </div>
      </section>

      <main className="map-view-shell">
        <section className="map-filter-row">
          {categories.map((category) => {
            const Icon = categoryIcons[category] || FaMapMarkerAlt;

            return (
              <button
                key={category}
                type="button"
                className={activeCategory === category ? "active" : ""}
                onClick={() => setActiveCategory(category)}
              >
                <Icon />
                {category}
              </button>
            );
          })}
        </section>

        <section className="map-view-grid">
          <div className="kimberley-map-card">
            <div className="map-type-toggle" role="group" aria-label="Map view type">
              {Object.entries(mapTypes).map(([key, config]) => {
                const ToggleIcon = config.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    className={mapType === key ? "active" : ""}
                    onClick={() => setMapType(key)}
                  >
                    <ToggleIcon />
                    <span>{config.label}</span>
                  </button>
                );
              })}
            </div>

            <MapContainer
              center={[kimberleyCenter.lat, kimberleyCenter.lng]}
              zoom={12}
              scrollWheelZoom
              className="kimberley-leaflet-map"
              aria-label="Kimberley map with location pins"
            >
              <TileLayer
                key={mapType}
                url={mapTypes[mapType].url}
                attribution={mapTypes[mapType].attribution}
                maxZoom={19}
              />
              <FlyToPlace place={selectedPlace} />

              {placedItems.map((item) => {
                const Icon = categoryIcons[item.category] || FaMapMarkerAlt;

                return (
                  <Marker
                    key={`${item.category}-${item.id || item.slug || item.title}`}
                    position={[item.coordinates.lat, item.coordinates.lng]}
                    icon={createPinIcon(Icon, activePlace?.title === item.title)}
                    eventHandlers={{ click: () => setSelectedPlace(item) }}
                  >
                    <Popup>{item.title}</Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>

          <aside className="map-place-panel">
            {activePlace ? (
              <>
                <div className="map-place-media">
                  {activePlace.image ? (
                    <img src={activePlace.image} alt={activePlace.title} />
                  ) : (
                    <FaMapMarkerAlt />
                  )}
                </div>
                <div className="map-place-content">
                  <span>{activePlace.category}</span>
                  <h2>{activePlace.title}</h2>
                  <p>{activePlace.description || "Kimberley destination."}</p>
                  <div className="map-place-meta">
                    {activePlace.location && (
                      <small>
                        <FaMapMarkerAlt />
                        {activePlace.location}
                      </small>
                    )}
                    <small>
                      <FaStar />
                      {Number(activePlace.rating) || 0}/5
                    </small>
                    {typeof activePlace.distanceKm === "number" && (
                      <small>
                        <FaRoute />
                        Approx. {activePlace.distanceKm.toFixed(1)} km from centre
                      </small>
                    )}
                    {activePlace.openStatus?.known && (
                      <small className={activePlace.openStatus.open ? "open" : "closed"}>
                        {activePlace.openStatus.label}
                      </small>
                    )}
                  </div>
                  {activePlace.detailPath && (
                    <button type="button" onClick={() => openDetails(activePlace)}>
                      View Details
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p>No map items available yet.</p>
            )}
          </aside>
        </section>

        <section className="map-list-section">
          <div>
            <h2>{placedItems.length} mapped locations</h2>
            <p>
              Pins use known Kimberley-area coordinates. Items without exact
              coordinates stay listed below until location data is added.
            </p>
          </div>

          {unplacedItems.length > 0 && (
            <div className="map-unplaced-list">
              <h3>{unplacedItems.length} awaiting map coordinates</h3>
              <div>
                {unplacedItems.slice(0, 8).map((item) => (
                  <button
                    key={`${item.category}-${item.id || item.slug || item.title}`}
                    type="button"
                    onClick={() => setSelectedPlace(item)}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MapView;
